import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Pencil, Trash2, ArrowLeft, ArrowRight, Eye, ClipboardList, Copy, MoreVertical, ExternalLink } from "lucide-react";
import { generateId } from "../../lib/utils";
import { pageApi } from "../../services/page.service";
import { useWorkspaceStore } from "../../stores/workspace.store";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

type QuestionType = "text" | "single" | "multiple";

interface FormQuestion {
  id: string;
  label: string;
  type: QuestionType;
  required: boolean;
  options: string[];
}

interface FormStep {
  id: string;
  title: string;
  content: string;
  questions: FormQuestion[];
}

interface FormTheme {
  primaryColor: string;
  textColor: string;
  backgroundColor: string;
  backgroundImageUrl?: string;
}

interface ManagedForm {
  id: string;
  publicToken: string;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  responsePageSlug?: string;
  responsePageId?: string;
  responsesCount: number;
  incompleteCount: number;
  pages: FormStep[];
  theme: FormTheme;
}

interface FormsManagerWidgetProps {
  component: any;
  onComponentChange?: (componentId: string, config: any) => void;
}

interface FormDraft {
  id?: string;
  publicToken?: string;
  name: string;
  description: string;
  pages: FormStep[];
  theme: FormTheme;
  responsePageSlug?: string;
  responsePageId?: string;
  responsesCount?: number;
  incompleteCount?: number;
}

const DEFAULT_THEME: FormTheme = {
  primaryColor: "#2563eb",
  textColor: "#0f172a",
  backgroundColor: "#eff6ff",
  backgroundImageUrl: "",
};

const createDefaultQuestion = (): FormQuestion => ({
  id: generateId(),
  label: "Nova pergunta",
  type: "text",
  required: false,
  options: ["Opção 1", "Opção 2"],
});

const createDefaultStep = (index: number): FormStep => ({
  id: generateId(),
  title: `Página ${index + 1}`,
  content: "Edite o conteúdo desta página.",
  questions: [createDefaultQuestion()],
});

const createDefaultDraft = (): FormDraft => ({
  name: "Novo formulário",
  description: "",
  pages: [createDefaultStep(0), createDefaultStep(1), createDefaultStep(2), createDefaultStep(3)],
  theme: { ...DEFAULT_THEME },
});

const createPublicToken = (): string => {
  const rand = Math.random().toString(36).slice(2, 10);
  return `frm_${Date.now().toString(36)}_${rand}`;
};

function sanitizeFieldKey(questionId: string): string {
  const base = `q_${questionId}`.toLowerCase().replace(/[^a-z0-9_]/g, "_");
  return base.length > 60 ? base.slice(0, 60) : base;
}

function flattenQuestions(form: Pick<ManagedForm, "pages"> | FormDraft): FormQuestion[] {
  return (form.pages || []).flatMap((p) => p.questions || []);
}

function getAnswerStatus(answers: Record<string, any>, form: ManagedForm | FormDraft): boolean {
  const requiredQuestions = flattenQuestions(form).filter((q) => q.required);
  if (requiredQuestions.length === 0) return true;

  return requiredQuestions.every((q) => {
    const value = answers[q.id];
    if (q.type === "multiple") {
      return Array.isArray(value) && value.length > 0;
    }
    return value !== undefined && value !== null && String(value).trim() !== "";
  });
}

export default function FormsManagerWidget({ component, onComponentChange }: FormsManagerWidgetProps) {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { currentWorkspace } = useWorkspaceStore();

  const [forms, setForms] = useState<ManagedForm[]>(() => component?.config?.forms || []);
  const [currentPageId, setCurrentPageId] = useState<string | null>(null);

  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [builderStepIndex, setBuilderStepIndex] = useState(0);
  const [editingFormId, setEditingFormId] = useState<string | null>(null);
  const [draft, setDraft] = useState<FormDraft>(createDefaultDraft());

  const [isAnswerOpen, setIsAnswerOpen] = useState(false);
  const [answerFormId, setAnswerFormId] = useState<string | null>(null);
  const [answerStepIndex, setAnswerStepIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [isSubmittingAnswer, setIsSubmittingAnswer] = useState(false);

  useEffect(() => {
    const currentForms = Array.isArray(component?.config?.forms) ? component.config.forms : [];
    const normalizedForms: ManagedForm[] = currentForms.map((f: ManagedForm) => ({
      ...f,
      publicToken: f.publicToken || createPublicToken(),
    }));

    setForms(normalizedForms);

    const changed = currentForms.some((f: ManagedForm, idx: number) => f.publicToken !== normalizedForms[idx].publicToken);
    if (changed && onComponentChange) {
      onComponentChange(component.id, {
        ...component.config,
        forms: normalizedForms,
      });
    }
  }, [component?.config?.forms]);

  useEffect(() => {
    const loadCurrentPage = async () => {
      if (!currentWorkspace?.id || !slug) return;
      try {
        const page = await pageApi.getOne(currentWorkspace.id, slug);
        setCurrentPageId(page.id);
      } catch (error) {
        console.error("Falha ao carregar página atual para formulários:", error);
      }
    };

    loadCurrentPage();
  }, [currentWorkspace?.id, slug]);

  const saveFormsConfig = (nextForms: ManagedForm[]) => {
    setForms(nextForms);
    if (!onComponentChange) return;
    onComponentChange(component.id, {
      ...component.config,
      forms: nextForms,
    });
  };

  const selectedForm = useMemo(() => {
    if (!answerFormId) return null;
    return forms.find((f) => f.id === answerFormId) || null;
  }, [answerFormId, forms]);

  const selectedDraftStep = draft.pages[builderStepIndex];
  const selectedAnswerStep = selectedForm?.pages?.[answerStepIndex];

  const createResponseColumns = (formLike: ManagedForm | FormDraft) => {
    const questionColumns = flattenQuestions(formLike).map((q) => ({
      field: sanitizeFieldKey(q.id),
      label: q.label || "Pergunta sem título",
      type: "text",
      visible: true,
    }));

    return [
      { field: "response_id", label: "Resposta", type: "text", visible: true },
      { field: "submitted_at", label: "Enviado em", type: "text", visible: true },
      { field: "status", label: "Status", type: "text", visible: true },
      ...questionColumns,
    ];
  };

  const createResponseSubPage = async (formLike: FormDraft): Promise<{ id: string; slug: string }> => {
    if (!currentWorkspace?.id || !currentPageId) {
      throw new Error("Não foi possível identificar a página atual do workspace.");
    }

    const tableComponent = {
      id: generateId(),
      type: "table",
      position: { x: 0, y: 0, w: 48, h: 16 },
      config: {
        title: `Respostas: ${formLike.name}`,
        columns: createResponseColumns(formLike),
        dataSource: {
          type: "static",
          data: [],
        },
      },
    };

    const page = await pageApi.create(currentWorkspace.id, {
      name: `${formLike.name} - respostas`,
      icon: "🧾",
      description: `Respostas do formulário ${formLike.name}`,
      parentId: currentPageId,
      components: [tableComponent],
    });

    return { id: page.id, slug: page.slug };
  };

  const syncResponsePageColumns = async (formLike: ManagedForm | FormDraft) => {
    if (!currentWorkspace?.id || !formLike.responsePageSlug) return;

    const page = await pageApi.getOne(currentWorkspace.id, formLike.responsePageSlug);
    const components = Array.isArray(page.components) ? [...page.components] : [];
    const tableIndex = components.findIndex((c: any) => c.type === "table");
    if (tableIndex < 0) return;

    components[tableIndex] = {
      ...components[tableIndex],
      config: {
        ...components[tableIndex].config,
        title: `Respostas: ${formLike.name}`,
        columns: createResponseColumns(formLike),
      },
    };

    await pageApi.updateData(currentWorkspace.id, formLike.responsePageSlug, { components });
  };

  const openCreateBuilder = () => {
    setEditingFormId(null);
    setDraft(createDefaultDraft());
    setBuilderStepIndex(0);
    setIsBuilderOpen(true);
  };

  const openEditBuilder = (form: ManagedForm) => {
    setEditingFormId(form.id);
    setDraft({
      id: form.id,
      publicToken: form.publicToken,
      name: form.name,
      description: form.description || "",
      pages: JSON.parse(JSON.stringify(form.pages || [])),
      theme: { ...DEFAULT_THEME, ...(form.theme || {}) },
      responsePageSlug: form.responsePageSlug,
      responsePageId: form.responsePageId,
      responsesCount: form.responsesCount,
      incompleteCount: form.incompleteCount,
    });
    setBuilderStepIndex(0);
    setIsBuilderOpen(true);
  };

  const removeForm = async (form: ManagedForm) => {
    if (!window.confirm(`Excluir o formulário \"${form.name}\"?`)) return;

    if (currentWorkspace?.id && form.responsePageSlug) {
      try {
        await pageApi.delete(currentWorkspace.id, form.responsePageSlug);
      } catch (error) {
        console.error("Falha ao excluir subpágina de respostas:", error);
      }
    }

    saveFormsConfig(forms.filter((f) => f.id !== form.id));
  };

  const addStep = () => {
    setDraft((prev) => ({
      ...prev,
      pages: [...prev.pages, createDefaultStep(prev.pages.length)],
    }));
    setBuilderStepIndex(draft.pages.length);
  };

  const removeStep = (stepId: string) => {
    if (draft.pages.length <= 1) return;

    const nextPages = draft.pages.filter((p) => p.id !== stepId);
    setDraft((prev) => ({ ...prev, pages: nextPages }));
    setBuilderStepIndex((prev) => Math.max(0, Math.min(prev, nextPages.length - 1)));
  };

  const updateStep = (stepId: string, patch: Partial<FormStep>) => {
    setDraft((prev) => ({
      ...prev,
      pages: prev.pages.map((p) => (p.id === stepId ? { ...p, ...patch } : p)),
    }));
  };

  const addQuestion = (stepId: string) => {
    setDraft((prev) => ({
      ...prev,
      pages: prev.pages.map((p) =>
        p.id === stepId
          ? {
              ...p,
              questions: [...(p.questions || []), createDefaultQuestion()],
            }
          : p,
      ),
    }));
  };

  const removeQuestion = (stepId: string, questionId: string) => {
    setDraft((prev) => ({
      ...prev,
      pages: prev.pages.map((p) => {
        if (p.id !== stepId) return p;
        const nextQuestions = (p.questions || []).filter((q) => q.id !== questionId);
        return {
          ...p,
          questions: nextQuestions.length > 0 ? nextQuestions : [createDefaultQuestion()],
        };
      }),
    }));
  };

  const updateQuestion = (stepId: string, questionId: string, patch: Partial<FormQuestion>) => {
    setDraft((prev) => ({
      ...prev,
      pages: prev.pages.map((p) => {
        if (p.id !== stepId) return p;
        return {
          ...p,
          questions: (p.questions || []).map((q) => (q.id === questionId ? { ...q, ...patch } : q)),
        };
      }),
    }));
  };

  const saveForm = async () => {
    if (!draft.name.trim()) {
      alert("Informe o nome do formulário.");
      return;
    }

    if (draft.pages.length === 0) {
      alert("Adicione ao menos uma página no formulário.");
      return;
    }

    try {
      if (editingFormId) {
        const updatedForms = forms.map((f) => {
          if (f.id !== editingFormId) return f;
          return {
            ...f,
            name: draft.name,
            description: draft.description,
            pages: draft.pages,
            theme: draft.theme,
            publicToken: f.publicToken || createPublicToken(),
            updatedAt: new Date().toISOString(),
          };
        });

        const updatedForm = updatedForms.find((f) => f.id === editingFormId);
        if (updatedForm) {
          await syncResponsePageColumns(updatedForm);
        }

        saveFormsConfig(updatedForms);
      } else {
        const responsePage = await createResponseSubPage(draft);
        const newForm: ManagedForm = {
          id: generateId(),
          publicToken: createPublicToken(),
          name: draft.name,
          description: draft.description,
          pages: draft.pages,
          theme: draft.theme,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          responsesCount: 0,
          incompleteCount: 0,
          responsePageId: responsePage.id,
          responsePageSlug: responsePage.slug,
        };

        saveFormsConfig([...forms, newForm]);
      }

      setIsBuilderOpen(false);
    } catch (error: any) {
      console.error("Erro ao salvar formulário:", error);
      alert(error?.response?.data?.message || error?.message || "Erro ao salvar formulário.");
    }
  };

  const openAnswerModal = (form: ManagedForm) => {
    setAnswerFormId(form.id);
    setAnswerStepIndex(0);
    setAnswers({});
    setIsAnswerOpen(true);
  };

  const getPublicFormLink = (form: ManagedForm): string => {
    return `${window.location.origin}/forms/${form.publicToken}`;
  };

  const copyPublicLink = async (form: ManagedForm) => {
    try {
      await navigator.clipboard.writeText(getPublicFormLink(form));
      alert("Link público copiado!");
    } catch (error) {
      console.error("Falha ao copiar link:", error);
      alert("Não foi possível copiar automaticamente. Copie manualmente o link exibido.");
    }
  };

  const saveResponse = async (forceComplete: boolean) => {
    if (!selectedForm || !currentWorkspace?.id || !selectedForm.responsePageSlug) return;

    const isComplete = getAnswerStatus(answers, selectedForm);
    if (forceComplete && !isComplete) {
      alert("Existem perguntas obrigatórias sem resposta.");
      return;
    }

    setIsSubmittingAnswer(true);
    try {
      const page = await pageApi.getOne(currentWorkspace.id, selectedForm.responsePageSlug);
      const components = Array.isArray(page.components) ? [...page.components] : [];
      const tableIndex = components.findIndex((c: any) => c.type === "table");

      if (tableIndex < 0) {
        throw new Error("Tabela de respostas não encontrada na subpágina.");
      }

      const currentRows = components[tableIndex]?.config?.dataSource?.data || [];
      const responseRow: Record<string, any> = {
        response_id: `resp_${Date.now()}`,
        submitted_at: new Date().toLocaleString("pt-BR"),
        status: isComplete ? "completo" : "incompleto",
      };

      flattenQuestions(selectedForm).forEach((q) => {
        const value = answers[q.id];
        if (q.type === "multiple") {
          responseRow[sanitizeFieldKey(q.id)] = Array.isArray(value) ? value.join(", ") : "";
        } else {
          responseRow[sanitizeFieldKey(q.id)] = value || "";
        }
      });

      components[tableIndex] = {
        ...components[tableIndex],
        config: {
          ...components[tableIndex].config,
          dataSource: {
            ...components[tableIndex].config?.dataSource,
            type: "static",
            data: [...currentRows, responseRow],
          },
        },
      };

      await pageApi.updateData(currentWorkspace.id, selectedForm.responsePageSlug, {
        components,
      });

      const nextForms = forms.map((f) => {
        if (f.id !== selectedForm.id) return f;
        return {
          ...f,
          responsesCount: (f.responsesCount || 0) + 1,
          incompleteCount: (f.incompleteCount || 0) + (isComplete ? 0 : 1),
          updatedAt: new Date().toISOString(),
        };
      });

      saveFormsConfig(nextForms);
      setIsAnswerOpen(false);
    } catch (error: any) {
      console.error("Erro ao salvar resposta:", error);
      alert(error?.response?.data?.message || error?.message || "Erro ao salvar resposta.");
    } finally {
      setIsSubmittingAnswer(false);
    }
  };

  const themeStyles = selectedForm
    ? {
        backgroundColor: selectedForm.theme?.backgroundColor || DEFAULT_THEME.backgroundColor,
        color: selectedForm.theme?.textColor || DEFAULT_THEME.textColor,
        backgroundImage: selectedForm.theme?.backgroundImageUrl
          ? `url(${selectedForm.theme.backgroundImageUrl})`
          : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }
    : {};

  if (!currentWorkspace?.id || !slug) {
    return (
      <Card>
        <CardContent className="p-4 text-sm text-muted-foreground">
          Carregando contexto do workspace para o gerenciador de formulários...
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="h-full space-y-4">
      <Card className="border">
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="text-lg">Gerenciador de Formulários</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Crie, edite e acompanhe formulários deste workspace.
              </p>
            </div>
            <Button onClick={openCreateBuilder} disabled={!onComponentChange}>
              <Plus className="mr-2 h-4 w-4" /> Novo formulário
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {forms.length === 0 ? (
            <div className="p-2 text-sm text-muted-foreground">
              Nenhum formulário criado ainda.
            </div>
          ) : (
            <div className="rounded-md border divide-y">
              {forms.map((form) => (
                <div key={form.id} className="px-3 py-3 flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <p className="text-sm font-semibold truncate">{form.name}</p>
                      <Badge variant="secondary" className="text-[11px] leading-none">
                        {form.responsesCount || 0} resp.
                      </Badge>
                      <Badge variant="outline" className="text-[11px] leading-none">
                        {form.incompleteCount || 0} inc.
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground truncate mt-1">
                      {form.description || "Sem descrição"}
                    </p>
                  </div>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem onClick={() => copyPublicLink(form)}>
                        <Copy className="mr-2 h-4 w-4" /> Copiar link
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => window.open(getPublicFormLink(form), "_blank") }>
                        <ExternalLink className="mr-2 h-4 w-4" /> Abrir link público
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openAnswerModal(form)}>
                        <ClipboardList className="mr-2 h-4 w-4" /> Responder
                      </DropdownMenuItem>
                      {form.responsePageSlug && (
                        <DropdownMenuItem onClick={() => navigate(`/pages/${form.responsePageSlug}`)}>
                          <Eye className="mr-2 h-4 w-4" /> Ver respostas
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => openEditBuilder(form)} disabled={!onComponentChange}>
                        <Pencil className="mr-2 h-4 w-4" /> Editar
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => removeForm(form)}
                        disabled={!onComponentChange}
                      >
                        <Trash2 className="mr-2 h-4 w-4" /> Excluir
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={isBuilderOpen} onOpenChange={setIsBuilderOpen}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingFormId ? "Editar formulário" : "Novo formulário"}</DialogTitle>
            <DialogDescription>
              Defina as páginas do formulário em carrossel, perguntas e tema visual.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Nome do formulário</Label>
                <Input
                  value={draft.name}
                  onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: Pesquisa de satisfação"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Descrição</Label>
                <Input
                  value={draft.description}
                  onChange={(e) => setDraft((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder="Breve descrição"
                />
              </div>
            </div>

            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">Páginas do formulário</CardTitle>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setBuilderStepIndex((p) => Math.max(0, p - 1))}
                      disabled={builderStepIndex === 0}
                    >
                      <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      {builderStepIndex + 1}/{draft.pages.length}
                    </span>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setBuilderStepIndex((p) => Math.min(draft.pages.length - 1, p + 1))
                      }
                      disabled={builderStepIndex === draft.pages.length - 1}
                    >
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {draft.pages.map((p, idx) => (
                    <Button
                      key={p.id}
                      type="button"
                      variant={idx === builderStepIndex ? "default" : "outline"}
                      size="sm"
                      onClick={() => setBuilderStepIndex(idx)}
                    >
                      {idx + 1}
                    </Button>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={addStep}>
                    <Plus className="mr-1 h-4 w-4" /> Adicionar página
                  </Button>
                  {draft.pages.length > 1 && selectedDraftStep && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-destructive"
                      onClick={() => removeStep(selectedDraftStep.id)}
                    >
                      <Trash2 className="mr-1 h-4 w-4" /> Remover página atual
                    </Button>
                  )}
                </div>

                {selectedDraftStep && (
                  <div className="grid gap-3">
                    <div className="space-y-1.5">
                      <Label>Título da página</Label>
                      <Input
                        value={selectedDraftStep.title}
                        onChange={(e) => updateStep(selectedDraftStep.id, { title: e.target.value })}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Conteúdo da página</Label>
                      <Textarea
                        rows={3}
                        value={selectedDraftStep.content}
                        onChange={(e) => updateStep(selectedDraftStep.id, { content: e.target.value })}
                      />
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label>Perguntas</Label>
                        <Button type="button" variant="outline" size="sm" onClick={() => addQuestion(selectedDraftStep.id)}>
                          <Plus className="mr-1 h-4 w-4" /> Pergunta
                        </Button>
                      </div>

                      {(selectedDraftStep.questions || []).map((question, qIndex) => (
                        <Card key={question.id}>
                          <CardContent className="p-3 space-y-3">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium">Pergunta {qIndex + 1}</p>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="text-destructive"
                                onClick={() => removeQuestion(selectedDraftStep.id, question.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                              <Input
                                className="md:col-span-2"
                                value={question.label}
                                onChange={(e) =>
                                  updateQuestion(selectedDraftStep.id, question.id, {
                                    label: e.target.value,
                                  })
                                }
                                placeholder="Texto da pergunta"
                              />
                              <select
                                className="h-10 rounded-md border bg-background px-3 text-sm"
                                value={question.type}
                                onChange={(e) =>
                                  updateQuestion(selectedDraftStep.id, question.id, {
                                    type: e.target.value as QuestionType,
                                  })
                                }
                              >
                                <option value="text">Texto</option>
                                <option value="single">Escolha única</option>
                                <option value="multiple">Múltipla escolha</option>
                              </select>
                            </div>

                            <label className="inline-flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                checked={question.required}
                                onChange={(e) =>
                                  updateQuestion(selectedDraftStep.id, question.id, {
                                    required: e.target.checked,
                                  })
                                }
                              />
                              Pergunta obrigatória
                            </label>

                            {(question.type === "single" || question.type === "multiple") && (
                              <div className="space-y-2">
                                <Label>Opções (uma por linha)</Label>
                                <Textarea
                                  rows={3}
                                  value={(question.options || []).join("\n")}
                                  onChange={(e) =>
                                    updateQuestion(selectedDraftStep.id, question.id, {
                                      options: e.target.value
                                        .split("\n")
                                        .map((o) => o.trim())
                                        .filter(Boolean),
                                    })
                                  }
                                />
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Tema da página</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Cor primária</Label>
                  <Input
                    type="color"
                    value={draft.theme.primaryColor}
                    onChange={(e) =>
                      setDraft((prev) => ({
                        ...prev,
                        theme: { ...prev.theme, primaryColor: e.target.value },
                      }))
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Cor do texto</Label>
                  <Input
                    type="color"
                    value={draft.theme.textColor}
                    onChange={(e) =>
                      setDraft((prev) => ({
                        ...prev,
                        theme: { ...prev.theme, textColor: e.target.value },
                      }))
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Cor de fundo</Label>
                  <Input
                    type="color"
                    value={draft.theme.backgroundColor}
                    onChange={(e) =>
                      setDraft((prev) => ({
                        ...prev,
                        theme: { ...prev.theme, backgroundColor: e.target.value },
                      }))
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Imagem de fundo (URL)</Label>
                  <Input
                    value={draft.theme.backgroundImageUrl || ""}
                    onChange={(e) =>
                      setDraft((prev) => ({
                        ...prev,
                        theme: { ...prev.theme, backgroundImageUrl: e.target.value },
                      }))
                    }
                    placeholder="https://..."
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBuilderOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={saveForm}>Salvar formulário</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isAnswerOpen} onOpenChange={setIsAnswerOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedForm?.name || "Responder formulário"}</DialogTitle>
            <DialogDescription>
              Navegue pelas páginas no carrossel e envie as respostas.
            </DialogDescription>
          </DialogHeader>

          {selectedForm && selectedAnswerStep && (
            <div className="space-y-4">
              <div className="rounded-lg p-4 border" style={themeStyles}>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold">{selectedAnswerStep.title}</h4>
                  <Badge variant="outline">
                    {answerStepIndex + 1}/{selectedForm.pages.length}
                  </Badge>
                </div>
                <p className="text-sm opacity-90">{selectedAnswerStep.content}</p>
              </div>

              <div className="space-y-3">
                {(selectedAnswerStep.questions || []).map((question) => {
                  const value = answers[question.id];
                  return (
                    <Card key={question.id}>
                      <CardContent className="p-4 space-y-2">
                        <div className="text-sm font-medium">
                          {question.label}
                          {question.required && <span className="text-destructive"> *</span>}
                        </div>

                        {question.type === "text" && (
                          <Input
                            value={value || ""}
                            onChange={(e) =>
                              setAnswers((prev) => ({ ...prev, [question.id]: e.target.value }))
                            }
                            placeholder="Sua resposta"
                          />
                        )}

                        {question.type === "single" && (
                          <div className="space-y-1">
                            {(question.options || []).map((opt, idx) => (
                              <label key={`${question.id}_${idx}`} className="flex items-center gap-2 text-sm">
                                <input
                                  type="radio"
                                  name={question.id}
                                  checked={value === opt}
                                  onChange={() =>
                                    setAnswers((prev) => ({ ...prev, [question.id]: opt }))
                                  }
                                />
                                {opt}
                              </label>
                            ))}
                          </div>
                        )}

                        {question.type === "multiple" && (
                          <div className="space-y-1">
                            {(question.options || []).map((opt, idx) => {
                              const selectedValues = Array.isArray(value) ? value : [];
                              const checked = selectedValues.includes(opt);
                              return (
                                <label key={`${question.id}_${idx}`} className="flex items-center gap-2 text-sm">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(e) => {
                                      setAnswers((prev) => {
                                        const current = Array.isArray(prev[question.id])
                                          ? [...prev[question.id]]
                                          : [];
                                        if (e.target.checked) {
                                          if (!current.includes(opt)) current.push(opt);
                                        } else {
                                          const idxToRemove = current.indexOf(opt);
                                          if (idxToRemove >= 0) current.splice(idxToRemove, 1);
                                        }
                                        return { ...prev, [question.id]: current };
                                      });
                                    }}
                                  />
                                  {opt}
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setAnswerStepIndex((p) => Math.max(0, p - 1))}
                    disabled={answerStepIndex === 0}
                  >
                    <ArrowLeft className="mr-1 h-4 w-4" /> Anterior
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() =>
                      setAnswerStepIndex((p) => Math.min(selectedForm.pages.length - 1, p + 1))
                    }
                    disabled={answerStepIndex === selectedForm.pages.length - 1}
                  >
                    Próxima <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    disabled={isSubmittingAnswer}
                    onClick={() => saveResponse(false)}
                  >
                    Salvar incompleto
                  </Button>
                  <Button disabled={isSubmittingAnswer} onClick={() => saveResponse(true)}>
                    Enviar resposta
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
